// fileAdapter — the pure boundary reader for a File's `showResource` envelope:
// `{ view: { resource: { title, mediaType, description, … }, timestamps } }`.
// Cast-free over `unknown` (wireShape); a missing resource is a typed
// Malformed, every optional field degrades to absent, never a fabricated value.

import { Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type { LoadedFile } from "./ports/resource.js";
import { fields, str } from "./wireShape.js";

// "" on the wire means "no description" (core FileDescription) — absent here.
const nonEmpty = (s: string | undefined): string | undefined =>
    s === undefined || s.trim() === "" ? undefined : s;

export const parseFileView = (
    value: unknown,
): Result.Result<LoadedFile, ExternalError> => {
    const view = fields(fields(value)?.get("view"));
    const resource = fields(view?.get("resource"));
    if (resource === undefined) {
        return Result.err(mkMalformed("showResource: no resource in view"));
    }
    const createdAt = str(fields(view?.get("timestamps"))?.get("createdAt"));
    return Result.ok({
        title: str(resource.get("title")),
        mediaType: nonEmpty(str(resource.get("mediaType"))),
        description: nonEmpty(str(resource.get("description"))),
        createdAt:
            createdAt !== undefined && !Number.isNaN(Date.parse(createdAt))
                ? createdAt
                : undefined,
    });
};
